import {
  FunctionComponent,
  FormEvent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react'
import styled, { css } from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import { useBucketListThemesLazyQuery } from '../../hooks/useQueries'
import { breakpoints, media } from '../../styles/media'

import Button from '../../components/display/Button'
import CloseButton from '../../components/display/CloseButton'
import InvisibleButton from '../../components/display/InvisibleButton'
import DialogModal from '../../components/display/DialogModal'
import LazyImage from '../../components/display/LazyImage'
import Modal from '../../components/display/Modal'
import Container from '../../components/layout/Container'
import CheckIcon from '../../components/icons/Check'
import CrossIcon from '../../components/icons/CrossOut'
import { PlaceholderCardWrapper } from '../../components/cards/PlaceholderCard'
import { Label, TextField } from '../../components/form'

import { StickyButtonWrapper } from './shared'

const Outer = styled(Container)`
  max-width: ${breakpoints.md}px;
  padding: 0;
`

const Content = styled.div`
  position: relative;
  background-color: white;
  min-height: 100vh;
  padding-top: 60px;
`

const Heading = styled.h1`
  margin: 0 0 20px 16px;
  font-size: 20px;
  color: #000000;
  text-transform: uppercase;
`

const ThemesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 160px;
`

export const selectMarkStyling = css`
  transition: color 150ms ease-out;
  border-radius: 50%;
`

export const SelectedMark = styled.div<{ hollow?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 8px;
  right: 8px;

  width: 22px;
  height: 22px;

  ${selectMarkStyling}
  border: 1px solid ${({ theme }) => theme.brand.jungle};

  svg {
    width: 10px;
    height: 10px;
    color: ${({ theme }) => theme.brand.jungle};
  }
  background-color: ${({ theme, hollow }) =>
    hollow ? theme.brand.sand : theme.brand.afterglow};
`

const ImagePlaceholder = styled(PlaceholderCardWrapper)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`

const ThemeOption = styled(InvisibleButton)`
  position: relative;
  min-height: 100px;
  width: calc(50% - 8px);
  background-color: lightgrey;
  margin-bottom: 16px;

  border-radius: 6px;
  overflow: hidden;

  &:nth-child(odd) {
    margin-right: 16px;
  }

  ${media.md`
    min-height: 240px;
  `}
`

const BucketlistForm = styled.form`
  display: flex;
  flex-direction: column;

  padding: 0 16px;
  border-top: solid 1px #d3d3d3;

  input,
  textarea {
    margin: 0;
  }

  ${Label} {
    margin: 30px 0 15px 0;
  }
`

const ConfirmDeleteDialog = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${Button} {
    width: 100px;
    margin: 0 16px;
  }
`

interface EditBucketlistModalProps {
  bucketlist?: {
    id: string
    name?: string
    theme?: {
      id: string
      name: string
    }
  }
  isOpen: boolean
  onSaveBucketlist: (arg0: {
    id?: string
    name: string
    themeId: string
  }) => any
  onDeleteBucketlist?: (id: string) => any
  onRequestClose: (...args: any) => any
  noCloseOnSubmit?: boolean
}

const EditBucketlistModal: FunctionComponent<EditBucketlistModalProps> = ({
  bucketlist,
  isOpen,
  onSaveBucketlist,
  onDeleteBucketlist,
  onRequestClose,
  noCloseOnSubmit,
}) => {
  const [name, setName] = useState(bucketlist?.name || '')
  const [themeId, setThemeId] = useState(bucketlist?.theme?.id || null)
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)

  const { md: isMd } = useMediaQueryContext()

  const [
    loadBucketListThemes,
    { data, loading },
  ] = useBucketListThemesLazyQuery()
  useEffect(() => {
    if (isOpen) {
      loadBucketListThemes()
    }
  }, [isOpen])

  const bucketlistThemes = !loading && data?.defaults?.bucketListThemes

  const handleNameChange = (e: { target: { value: string } }) =>
    setName(e.target.value)
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSaveBucketlist({ id: bucketlist && bucketlist.id, name, themeId })
    if (!bucketlist || !bucketlist.id) {
      setName('')
      setThemeId(null)
    }

    if (!noCloseOnSubmit) {
      onRequestClose()
    }
  }
  const handleDelete = (e: SyntheticEvent) => {
    e.preventDefault()
    onDeleteBucketlist(bucketlist.id)
    setConfirmDeleteModalOpen(false)
    onRequestClose()
  }

  const headingText = bucketlist ? 'Edit Bucket List' : 'Create a Bucket List'
  const submitButtonText = bucketlist ? 'Save' : 'Create'
  const disableSubmit = name.length === 0 || !themeId

  return (
    <Modal
      className="ReactModal__Content c-fullscreen-modal__content"
      overlayClassName="ReactModal__Overlay c-fullscreen-modal__overlay"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <Outer>
        <Content>
          <CloseButton onClick={onRequestClose}>
            <CrossIcon />
          </CloseButton>
          <Heading>{headingText}</Heading>
          <BucketlistForm>
            <Label htmlFor="bucketlist-name">Name</Label>
            <TextField
              id="bucketlist-name"
              tabIndex={0}
              onChange={handleNameChange}
              value={name}
              placeholder="Give your bucket list a name"
            />
            <Label htmlFor="bucketlist-feature-image">Feature image</Label>
            <ThemesWrapper>
              {!loading &&
                bucketlistThemes?.map(({ id, name: themeName, image }) => (
                  <ThemeOption
                    key={id}
                    aria-label={themeName}
                    onClick={() => setThemeId(id)}
                  >
                    <>
                      {themeId === id && (
                        <SelectedMark>
                          <CheckIcon />
                        </SelectedMark>
                      )}
                      <LazyImage
                        className="lazyload"
                        data-src={isMd ? image.large : image.medium}
                        rounded
                      />
                      <ImagePlaceholder />
                    </>
                  </ThemeOption>
                ))}
            </ThemesWrapper>
          </BucketlistForm>
        </Content>
        <StickyButtonWrapper>
          <Button onClick={handleSubmit} disabled={disableSubmit}>
            {submitButtonText}
          </Button>
          {bucketlist && onDeleteBucketlist && (
            <Button
              onClick={() => setConfirmDeleteModalOpen(true)}
              ghost
              noBorder
            >
              Delete
            </Button>
          )}
        </StickyButtonWrapper>
        {bucketlist && (
          <DialogModal
            isOpen={confirmDeleteModalOpen}
            onRequestClose={() => setConfirmDeleteModalOpen(false)}
            title={`Delete ${bucketlist.name}`}
          >
            <ConfirmDeleteDialog>
              <Button
                ghost
                noBorder
                onClick={() => setConfirmDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleDelete}>Yes</Button>
            </ConfirmDeleteDialog>
          </DialogModal>
        )}
      </Outer>
    </Modal>
  )
}

export default EditBucketlistModal
